import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const OTP_TTL_MINUTES = 10;
const OTP_RECIPIENT = "vkmujrani1999@gmail.com"; // hardcoded, never overridable

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  const roles = (data ?? []).map((r) => r.role);
  if (!roles.includes("admin")) throw new Error("Admin only");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function generate6DigitCode(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const n = new DataView(bytes.buffer).getUint32(0);
  return String(n % 1_000_000).padStart(6, "0");
}

/** Zero-data guard exposed for UI pre-check (also re-checked in initiate/confirm). */
export const checkDeleteEmployeeEligibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ user_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { data: r, error } = await supabaseAdmin.rpc("has_any_linked_data", {
      _user_id: data.user_id,
    });
    if (error) throw new Error(error.message);
    return { hasData: !!r };
  });

export const initiateDeleteEmployee = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ user_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.user_id === context.userId) throw new Error("Cannot delete yourself");

    // Guard: zero linked data required
    const { data: hasData, error: guardErr } = await supabaseAdmin.rpc(
      "has_any_linked_data",
      { _user_id: data.user_id },
    );
    if (guardErr) throw new Error(guardErr.message);
    if (hasData) {
      throw new Error(
        "This employee has linked data. Deactivate instead of deleting.",
      );
    }

    const [{ data: target }, { data: requester }] = await Promise.all([
      supabaseAdmin
        .from("profiles")
        .select("full_name, email")
        .eq("id", data.user_id)
        .maybeSingle(),
      supabaseAdmin
        .from("profiles")
        .select("full_name, email")
        .eq("id", context.userId)
        .maybeSingle(),
    ]);
    if (!target) throw new Error("Employee not found");

    const code = generate6DigitCode();
    const otpHash = await sha256Hex(code);
    const expiresAt = new Date(Date.now() + OTP_TTL_MINUTES * 60_000).toISOString();

    // Invalidate previous unused OTPs for this target
    await supabaseAdmin
      .from("delete_employee_otps")
      .update({ used_at: new Date().toISOString() })
      .eq("target_user_id", data.user_id)
      .is("used_at", null);

    const { data: otpRow, error: otpErr } = await supabaseAdmin
      .from("delete_employee_otps")
      .insert({
        target_user_id: data.user_id,
        requested_by: context.userId,
        otp_hash: otpHash,
        expires_at: expiresAt,
      })
      .select("id")
      .single();
    if (otpErr || !otpRow) throw new Error(otpErr?.message ?? "OTP create failed");

    // Send via internal transactional email route, forwarding the caller's bearer.
    const req = getRequest();
    const origin = new URL(req.url).origin;
    const authHeader = req.headers.get("authorization") ?? "";

    const sendRes = await fetch(`${origin}/lovable/email/transactional/send`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: authHeader },
      body: JSON.stringify({
        templateName: "delete-employee-otp",
        // template.to overrides recipientEmail — kept for API completeness
        recipientEmail: OTP_RECIPIENT,
        idempotencyKey: `delete-employee-${otpRow.id}`,
        templateData: {
          code,
          targetName: target.full_name ?? "Unknown",
          targetEmail: target.email ?? "",
          requestedBy: requester?.full_name ?? requester?.email ?? "Admin",
        },
      }),
    });
    if (!sendRes.ok) {
      const t = await sendRes.text().catch(() => "");
      console.error("[delete-employee] email send failed", sendRes.status, t);
      throw new Error("Failed to send verification email");
    }

    return { otp_id: otpRow.id, sent_to_masked: "v***@gmail.com" };
  });

export const confirmDeleteEmployee = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        user_id: z.string().uuid(),
        otp_id: z.string().uuid(),
        otp_code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.user_id === context.userId) throw new Error("Cannot delete yourself");

    // Race guard: re-check zero-data at confirm time
    const { data: hasData, error: guardErr } = await supabaseAdmin.rpc(
      "has_any_linked_data",
      { _user_id: data.user_id },
    );
    if (guardErr) throw new Error(guardErr.message);
    if (hasData) {
      throw new Error(
        "This employee has linked data. Deactivate instead of deleting.",
      );
    }

    const { data: otp, error: otpErr } = await supabaseAdmin
      .from("delete_employee_otps")
      .select("id, target_user_id, otp_hash, expires_at, used_at")
      .eq("id", data.otp_id)
      .maybeSingle();
    if (otpErr) throw new Error(otpErr.message);
    if (!otp) throw new Error("Verification code not found");
    if (otp.target_user_id !== data.user_id) throw new Error("Code does not match this employee");
    if (otp.used_at) throw new Error("Verification code already used");
    if (new Date(otp.expires_at).getTime() < Date.now())
      throw new Error("Verification code expired");

    const expectedHash = await sha256Hex(data.otp_code);
    if (expectedHash !== otp.otp_hash) throw new Error("Incorrect verification code");

    // Atomically mark OTP used
    const { data: consumed, error: consumeErr } = await supabaseAdmin
      .from("delete_employee_otps")
      .update({ used_at: new Date().toISOString() })
      .eq("id", otp.id)
      .is("used_at", null)
      .select("id")
      .maybeSingle();
    if (consumeErr) throw new Error(consumeErr.message);
    if (!consumed) throw new Error("Verification code already used");

    // Snapshot before delete for audit
    const { data: before } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, email, department, employee_id, status")
      .eq("id", data.user_id)
      .maybeSingle();

    // Audit BEFORE delete
    await supabaseAdmin.from("audit_log").insert({
      action_type: "user.hard_delete",
      admin_id: context.userId,
      target_user_id: data.user_id,
      before_data: before ?? null,
      after_data: { otp_id: otp.id, verified_via: OTP_RECIPIENT },
    });

    const { error: delErr } = await supabaseAdmin.auth.admin.deleteUser(data.user_id);
    if (delErr) throw new Error(delErr.message);

    return { ok: true };
  });
